import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'onboarding_v1';

/**
 * useTutorial — manages the onboarding tutorial state.
 *
 * Auto-starts once for new users after a child is selected.
 * Completion is persisted both in localStorage (fast local check) and
 * Firestore (cross-device, via the onComplete callback from useDashboardSettings).
 *
 * Args:
 *   hasChild          — boolean; tour only starts when a child is selected
 *   tourCompleted     — boolean from Firestore (useDashboardSettings); prevents
 *                       re-running on other devices even after localStorage cleared
 *   onComplete        — async fn to persist completion to Firestore
 *
 * Returns:
 *   active     — boolean, whether the tutorial is currently showing
 *   stepIndex  — 0-based index of the current step
 *   next(total) — advance to next step (or complete if last)
 *   skip()     — dismiss and mark complete
 *   restart()  — re-run from step 0 (clears localStorage + Firestore flag)
 */
export default function useTutorial(hasChild, { tourCompleted = false, onComplete } = {}) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Auto-start once when a child is available and the tour hasn't been completed
  // on any device (Firestore) or locally (localStorage).
  useEffect(() => {
    if (hasChild && !tourCompleted && !localStorage.getItem(STORAGE_KEY)) {
      // Small delay so the dashboard has time to render all target elements
      const t = setTimeout(() => setActive(true), 800);
      return () => clearTimeout(t);
    }
  }, [hasChild, tourCompleted]);

  const complete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'done');
    onComplete?.();
    setActive(false);
    setStepIndex(0);
  }, [onComplete]);

  const next = useCallback((total) => {
    setStepIndex((i) => {
      if (i >= total - 1) {
        complete();
        return 0;
      }
      return i + 1;
    });
  }, [complete]);

  const skip = useCallback(() => {
    complete();
  }, [complete]);

  const restart = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setStepIndex(0);
    setActive(true);
  }, []);

  return { active, stepIndex, next, skip, restart };
}
